import BaseApi, { API_BASE_URL, ApiResponse } from './base';

export interface Category {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  parent?: Category;
  children?: Category[];
  createdAt: string;
  updatedAt: string;
}

export interface CategoryTreeNode extends Category {
  children: CategoryTreeNode[];
}

export interface CreateCategoryRequest {
  name: string;
  description?: string;
  parentId?: string;
}

export interface UpdateCategoryRequest {
  name?: string;
  description?: string;
  parentId?: string | null;
}

export interface GetCategoriesParams {
  parentOnly?: boolean;
}

class CategoriesApi extends BaseApi {
  constructor(baseURL: string = API_BASE_URL) {
    super(baseURL);
  }

  /**
   * Get categories. When parentOnly is false (default), returns a hierarchical tree.
   * When parentOnly is true, returns only root categories as a flat list.
   */
  async getCategories(params?: GetCategoriesParams): Promise<ApiResponse<Category[] | CategoryTreeNode[]>> {
    const queryParams = new URLSearchParams();
    if (params?.parentOnly) {
      queryParams.append('parent_only', 'true');
    }
    const queryString = queryParams.toString();
    return this.get<Category[] | CategoryTreeNode[]>(`/categories${queryString ? `?${queryString}` : ''}`);
  }

  async getCategory(id: string): Promise<ApiResponse<Category>> {
    return this.get<Category>(`/categories/${id}`);
  }

  async createCategory(data: CreateCategoryRequest): Promise<ApiResponse<Category>> {
    return this.post<Category>('/categories', data);
  }

  async updateCategory(id: string, data: UpdateCategoryRequest): Promise<ApiResponse<Category>> {
    return this.patch<Category>(`/categories/${id}`, data);
  }

  async deleteCategory(id: string): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/categories/${id}`);
  }
}

export default new CategoriesApi();
